import {
    BellDot,
    Building2,
    CircleCheckBig,
    CircleSlash2,
    Dot,
    File,
    FileInput,
    Inbox,
    MessageSquarePlus,
    MessageSquareQuote,
    Send,
    Trash2,
    UserPen,
} from "lucide-react";
import { Link } from "react-router";
import { twMerge } from "tailwind-merge";
import { generateJSON, generateText } from "@tiptap/core";
import { previewExtensions } from "@editor/index";
import { NotificationType, type Notification } from "../../clients/Purchaser/Notifications";
import DateUtil from "../../util/DateUtil";
import { Button, Text } from "../DesignSystem";

type NotificationItemProps = {
    notification: Notification;
    markAsRead: (id: number) => void;
    markAsUnread: (id: number) => void;
    deleteNotification: (id: number) => void;
    condensed?: boolean;
};

export default function NotificationItem({ notification, markAsRead, markAsUnread, deleteNotification, condensed = false }: NotificationItemProps) {
    const event = notification.event;
    const hasAttachments = !!event.attachmentCount && event.attachmentCount > 0;
    const textContent = event.content ? generateText(generateJSON(event.content, previewExtensions), previewExtensions) : null;
    const notificationTypeLinkMap = getNotificationTypeLinkMap({
        rfqUniquePublicId: event.rfqUniquePublicId,
        mailboxId: event.mailboxId,
        requisitionId: event.requisitionId,
    });

    return (
        <Link
            to={notificationTypeLinkMap[event.type]}
            className="group relative flex w-full cursor-pointer flex-row gap-1.5 rounded-md px-4 py-2 hover:bg-stone-100"
            onClick={() => markAsRead(notification.id)}
        >
            <NotificationIcon type={notification.event.type} isRead={notification.isRead} />
            <div className="flex w-full flex-col gap-1">
                <div>
                    <div className="flex h-6 items-center">
                        <Text variant="smallBody700" className="line-clamp-1 flex-1 truncate text-wrap text-stone-950">
                            {event.title}
                        </Text>
                        <Text variant="smallBody500" className="items-end text-stone-500">
                            {DateUtil.formatted(notification.createdAt, {
                                custom: "Relative",
                            })}
                        </Text>
                    </div>
                    <Text variant="smallBody500" className="line-clamp-1 truncate text-wrap text-stone-950">
                        {event.subtitle}
                    </Text>
                </div>
                {!condensed && (
                    <>
                        {textContent && (
                            <Text
                                variant="smallBody500"
                                className={twMerge([
                                    // base
                                    "line-clamp-2 text-wrap text-stone-500",
                                    // if there are attachments
                                    hasAttachments ? "line-clamp-2" : "line-clamp-3",
                                ])}
                            >
                                {textContent}
                            </Text>
                        )}
                        {hasAttachments && (
                            <div className="flex w-fit flex-row items-center gap-1 rounded-lg border border-stone-200 p-1.5">
                                <File className="h-4 w-4 shrink-0 stroke-stone-400" />
                                <Text variant="smallBody400" className="text-stone-700">
                                    {event.attachmentCount} File{event.attachmentCount === 1 ? "" : "s"} Attached
                                </Text>
                            </div>
                        )}
                    </>
                )}
            </div>
            {!condensed && (
                <div className="absolute top-3 right-4 hidden rounded-md border border-stone-200 bg-white group-hover:flex">
                    {!notification.isRead && (
                        <Button
                            color="clear"
                            icon={<CircleCheckBig className="h-4 w-4 shrink-0 stroke-stone-600" />}
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                markAsRead(notification.id);
                            }}
                        />
                    )}
                    {notification.isRead && (
                        <Button
                            color="clear"
                            icon={<BellDot className="h-4 w-4 shrink-0 stroke-stone-600" />}
                            onClick={e => {
                                e.preventDefault();
                                e.stopPropagation();
                                markAsUnread(notification.id);
                            }}
                        />
                    )}
                    <Button
                        color="clear"
                        icon={<Trash2 className="h-4 w-4 shrink-0 stroke-stone-600" />}
                        onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            deleteNotification(notification.id);
                        }}
                    />
                </div>
            )}
        </Link>
    );
}

type GetNotificationTypeLinkMapInput = {
    rfqUniquePublicId?: string;
    mailboxId?: number;
    requisitionId?: string;
};

function getNotificationTypeLinkMap({ rfqUniquePublicId, mailboxId, requisitionId }: GetNotificationTypeLinkMapInput) {
    return {
        // Requisitions
        [NotificationType.REQUISITION_ASSIGNED]: `/requisitions/${requisitionId}`,
        [NotificationType.REQUISITION_MESSAGE_RECEIVED]: `/requisitions/${requisitionId}`,
        [NotificationType.REQUISITION_NEW]: `/requisitions/${requisitionId}`,
        // RFQs
        [NotificationType.RFQ_BROADCAST_SENT]: `/rfqs/${rfqUniquePublicId}/inbox/${mailboxId}`,
        [NotificationType.RFQ_NO_QUOTE_RECEIVED]: `/rfqs/${rfqUniquePublicId}/inbox/${mailboxId}`,
        [NotificationType.RFQ_QUOTE_RECEIVED]: `/rfqs/${rfqUniquePublicId}/vendors/${mailboxId}/leveled-items`,
        [NotificationType.RFQ_STATUS_CHANGED]: `/rfqs/${rfqUniquePublicId}/summary`,
        [NotificationType.RFQ_VENDOR_ADDED]: `/rfqs/${rfqUniquePublicId}/inbox/${mailboxId}`,
        [NotificationType.RFQ_VENDOR_RESPONSE_RECEIVED]: `/rfqs/${rfqUniquePublicId}/inbox/${mailboxId}`,
    };
}

const NOTIFICATION_TYPE_ICON_MAP = {
    // Requisitions
    [NotificationType.REQUISITION_ASSIGNED]: UserPen,
    [NotificationType.REQUISITION_MESSAGE_RECEIVED]: MessageSquarePlus,
    [NotificationType.REQUISITION_NEW]: FileInput,
    // RFQs
    [NotificationType.RFQ_BROADCAST_SENT]: Send,
    [NotificationType.RFQ_NO_QUOTE_RECEIVED]: CircleSlash2,
    [NotificationType.RFQ_QUOTE_RECEIVED]: MessageSquareQuote,
    [NotificationType.RFQ_STATUS_CHANGED]: CircleCheckBig,
    [NotificationType.RFQ_VENDOR_ADDED]: Building2,
    [NotificationType.RFQ_VENDOR_RESPONSE_RECEIVED]: Inbox,
};

type NotificationIconProps = {
    type: NotificationType;
    isRead: boolean;
};

function NotificationIcon({ type, isRead }: NotificationIconProps) {
    const InnerIcon = NOTIFICATION_TYPE_ICON_MAP[type];

    if (isRead) {
        return (
            <div>
                <div className="rounded-2xl bg-white p-1.5">
                    <InnerIcon className="h-4 w-4 shrink-0 stroke-stone-400" />
                </div>
            </div>
        );
    }

    return (
        <div className="relative">
            <Dot className="stroke-bronze-600 bg-bronze-600 absolute top-0 left-0 h-2 w-2 shrink-0 rounded-2xl" />
            <div className="bg-bronze-200 rounded-2xl p-1.5">
                <InnerIcon className="stroke-bronze-600 h-4 w-4 shrink-0" />
            </div>
        </div>
    );
}
